import React, { useEffect, useState } from 'react';
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography } from '@mui/material';
import InlineLoader from '../../../components/common/InlineLoader';

export interface CheckInDialogProps {
  open: boolean;
  clientName: string;
  type: '2_week' | '10_week';
  onClose: () => void;
  /** Resolves when saved; rejects to keep the dialog open with an error. */
  onSubmit: (notes: string) => Promise<void>;
}

/** The dashboard's check-in shortcut: the notes are the check-in, so they are required. */
export const CheckInDialog: React.FC<CheckInDialogProps> = ({ open, clientName, type, onClose, onSubmit }) => {
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const label = type === '2_week' ? '2-week' : '10-week';

  useEffect(() => {
    if (open) {
      setNotes('');
      setError('');
    }
  }, [open]);

  const submit = async () => {
    if (!notes.trim()) return;
    setSaving(true);
    setError('');
    try {
      await onSubmit(notes);
    } catch {
      setError('This check-in was not saved. Try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Log the {label} check-in</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
          {clientName}
        </Typography>
        <TextField
          autoFocus
          fullWidth
          multiline
          minRows={4}
          label="How is the client getting on?"
          value={notes}
          onChange={(event) => setNotes(event.target.value)}
          disabled={saving}
          required
        />
        {error ? (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={submit} disabled={saving || !notes.trim()}>
          {saving ? <InlineLoader size={20} /> : 'Save check-in'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
